import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    InputAdornment,
    Alert
} from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';


interface CreateDeveloperDialogProps {
    open: boolean;
    onClose: () => void;
    onUserCreated: () => void;
}

const CreateDeveloperDialog: React.FC<CreateDeveloperDialogProps> = ({ open, onClose, onUserCreated }) => {
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
    });
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [createdUser, setCreatedUser] = useState<{ apiKey: string, message: string } | null>(null);
    const { showSnackbar, snackbarType } = useSnackbar();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleCreate = async () => {
        console.log('handleCreate called with:', formData);
        setLoading(true);
        let response: any;
        try {
            response = await clientService.api.createDeveloperApiV1DevelopersPost({
                ...formData,
                tnc_checked: true
            });
            if (response.ok) {
                setCreatedUser({
                    apiKey: response.data.api_key || '',
                    message: response.data.message
                });
                showSnackbar({ message: 'Developer created successfully', type: snackbarType.SUCCESS });
                onUserCreated();
            }
        } catch (error: any) {
            const errorMessage = error?.error?.message || 'Something went wrong';
            showSnackbar({ message: errorMessage, type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ email: '', first_name: '', last_name: '', password: '' });
        setCreatedUser(null);
        onClose();
    };

    const handleCopyApiKey = () => {
        if (createdUser?.apiKey) {
            navigator.clipboard.writeText(createdUser.apiKey);
            showSnackbar({ message: 'API Key copied to clipboard', type: snackbarType.INFO });
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            {!createdUser && <DialogTitle>Create Developer</DialogTitle>}
            <DialogContent>
                {createdUser ? (
                    <Box sx={{ mt: 2 }}>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {createdUser.message}
                        </Alert>
                        <Typography variant="subtitle2" gutterBottom>
                            API Key (Copy this now, it won't be shown again):
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            bgcolor: 'grey.100', 
                            p: 2, 
                            borderRadius: 1,
                            wordBreak: 'break-all'
                        }}>
                            <Typography variant="body2" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                                {createdUser.apiKey}
                            </Typography>
                            <IconButton onClick={handleCopyApiKey} size="small">
                                <ContentCopy />
                            </IconButton>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        
                        <TextField
                            label="First Name"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            fullWidth
                            required
                            autoComplete="off"
                        />
                        <TextField
                            label="Last Name"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            fullWidth
                            required
                            autoComplete="off"
                        />
                        <TextField
                            label="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            fullWidth
                            required
                            type="email"
                            autoComplete="off"
                        />
                        <TextField
                            label="Password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                            required
                            type={showPassword ? 'text' : 'password'}
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {createdUser ? 'Close' : 'Cancel'}
                </Button>
                {!createdUser && (
                    <Button 
                        onClick={handleCreate} 
                        variant="contained" 
                        disabled={loading || !formData.email || !formData.password}
                    >
                        {loading ? <>Creating...<CircularProgress size={24}/></> : 'Create'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CreateDeveloperDialog;
